import React, { useEffect, useRef, useState } from "react";
import requestFrame from "../../../shared/request-frame.js";
import { formatClock, type Timeline as TimelineData } from "./replay-timeline";
import styles from "./Timeline.module.scss";

// The replay's scrubber, carrying the round: a tick above the bar for every crash
// (that player's colour, the viewer's own raised and named), a diamond below it
// for every power-up grabbed, the countdown hatched so it can be skipped. Hover a
// mark to read it; click or drag anywhere to seek. Pointer events, so touch works.

const H = 44, BAR_Y = 24, PAD = 10;

interface Props {
  timeline: TimelineData;
  progress: () => number;            // 0..1
  onScrubStart?: () => void;         // grabbed: the player pauses
  onSeek: (progress: number) => void;
}

export default function Timeline({ timeline, progress, onScrubStart, onSeek }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const propsRef = useRef({ timeline, progress, onScrubStart, onSeek });
  propsRef.current = { timeline, progress, onScrubStart, onSeek };
  const pointer = useRef({ down: false, hover: false, p: 0 });
  const [tip, setTip] = useState<{ left: number; text: string } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    let raf = 0, width = 0;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const fit = () => { width = canvas.offsetWidth; canvas.width = Math.round(width * dpr); canvas.height = Math.round(H * dpr); };
    const xOf = (p: number) => PAD + p * (width - 2 * PAD);
    const pOf = (clientX: number) => Math.max(0, Math.min(1, (clientX - canvas.getBoundingClientRect().left - PAD) / Math.max(1, width - 2 * PAD)));

    function draw() {
      const { timeline: tl, progress: getP } = propsRef.current;
      const ctx = canvas.getContext("2d")!;
      const ptr = pointer.current;
      const p = ptr.down ? ptr.p : Math.max(0, Math.min(1, getP()));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, H);
      const thick = ptr.hover || ptr.down ? 5 : 4;
      // the bar
      ctx.lineCap = "round";
      ctx.lineWidth = thick;
      ctx.strokeStyle = "#2a3650";
      ctx.beginPath(); ctx.moveTo(xOf(0), BAR_Y); ctx.lineTo(xOf(1), BAR_Y); ctx.stroke();
      // played
      ctx.strokeStyle = "#28c4d4";
      ctx.beginPath(); ctx.moveTo(xOf(0), BAR_Y); ctx.lineTo(xOf(p), BAR_Y); ctx.stroke();
      // the countdown, hatched over whatever is under it
      if (tl.duration > 0 && tl.countdown > 0) {
        const end = xOf(tl.countdown / tl.duration);
        ctx.lineCap = "butt"; ctx.lineWidth = thick; ctx.strokeStyle = "rgba(7, 10, 18, 0.7)";
        for (let x = xOf(0) + 3; x < end; x += 7) { ctx.beginPath(); ctx.moveTo(x, BAR_Y); ctx.lineTo(Math.min(end, x + 3), BAR_Y); ctx.stroke(); }
      }
      // marks
      for (const m of tl.marks) {
        const x = xOf(tl.duration > 0 ? m.time / tl.duration : 0);
        ctx.fillStyle = m.you ? "#ff2e88" : m.color;
        if (m.kind === "crash") {
          const top = m.you ? 2 : 9, w = m.you ? 4 : 3;
          ctx.globalAlpha = m.you ? 1 : 0.9;
          ctx.fillRect(x - w / 2, top, w, BAR_Y - 4 - top);
        } else {
          ctx.globalAlpha = 0.95;
          ctx.save(); ctx.translate(x, BAR_Y + 12); ctx.rotate(Math.PI / 4); ctx.fillRect(-3.2, -3.2, 6.4, 6.4); ctx.restore();
        }
      }
      ctx.globalAlpha = 1;
      // the knob
      ctx.fillStyle = "#eaf0ff";
      ctx.beginPath(); ctx.arc(xOf(p), BAR_Y, ptr.hover || ptr.down ? 8 : 7, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#28c4d4";
      ctx.beginPath(); ctx.arc(xOf(p), BAR_Y, 4, 0, Math.PI * 2); ctx.fill();
      raf = requestFrame(draw);
    }

    function markNear(clientX: number): { left: number; text: string } | null {
      const { timeline: tl } = propsRef.current;
      const px = clientX - canvas.getBoundingClientRect().left;
      let best: any = null, bestD = 9;
      for (const m of tl.marks) {
        const d = Math.abs(xOf(tl.duration > 0 ? m.time / tl.duration : 0) - px);
        if (d < bestD) { bestD = d; best = m; }
      }
      return best ? { left: xOf(best.time / (tl.duration || 1)), text: formatClock(best.time) + "  " + best.label } : null;
    }

    const down = (e: PointerEvent) => {
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      pointer.current.down = true; pointer.current.p = pOf(e.clientX);
      propsRef.current.onScrubStart?.();
    };
    const move = (e: PointerEvent) => {
      if (pointer.current.down) { pointer.current.p = pOf(e.clientX); propsRef.current.onSeek(pointer.current.p); }
      const t = markNear(e.clientX);
      setTip((prev) => (prev && t && prev.text === t.text ? prev : t));
    };
    const up = (e: PointerEvent) => {
      if (!pointer.current.down) return;
      pointer.current.down = false;
      propsRef.current.onSeek(pOf(e.clientX));
    };
    const enter = () => { pointer.current.hover = true; };
    const leave = () => { pointer.current.hover = false; setTip(null); };

    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(canvas);
    canvas.addEventListener("pointerdown", down);
    canvas.addEventListener("pointermove", move);
    canvas.addEventListener("pointerup", up);
    canvas.addEventListener("pointercancel", up);
    canvas.addEventListener("pointerenter", enter);
    canvas.addEventListener("pointerleave", leave);
    raf = requestFrame(draw);
    return () => {
      window.cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      canvas.removeEventListener("pointerdown", down);
      canvas.removeEventListener("pointermove", move);
      canvas.removeEventListener("pointerup", up);
      canvas.removeEventListener("pointercancel", up);
      canvas.removeEventListener("pointerenter", enter);
      canvas.removeEventListener("pointerleave", leave);
    };
  }, []);

  const you = timeline.marks.find((m) => m.you);
  return (
    <div className={styles.timeline}>
      {you && timeline.duration > 0 && <span className={styles.you} style={{ left: `calc(${PAD}px + (100% - ${2 * PAD}px) * ${you.time / timeline.duration})` }}>You · {formatClock(you.time)}</span>}
      {tip && <span className={styles.tip} style={{ left: tip.left }}>{tip.text}</span>}
      <canvas ref={canvasRef} className={styles.canvas} style={{ height: H }} aria-label="Replay timeline" />
    </div>
  );
}
